import Money from "../ui/Money"
import { useEffect, useState } from "react"
import { SpinnerText } from "../ui/Spinner"
import { FiChevronDown } from "react-icons/fi"
import {
  getPatentFeeBreakdown,
  getPatentFeePreview,
  PatentFeeBreakdown,
  PatentFeeGroup
} from "../../services/stripePaymentService"
import { formatApiError } from "../../utils/formatApiError"
import { useFilingSidebarPanel } from "../ui/FilingSidebarAccordion"

export default function PatentApplicationFeesBox({
  applicationId,
  procedureType = "EP1001",
  refreshToken = 0
}: {
  applicationId: string | null
  procedureType?: "EP1001" | "EP1200"
  refreshToken?: number
}) {
  const [open, toggleOpen] = useFilingSidebarPanel("fees")
  const [breakdown, setBreakdown] = useState<PatentFeeBreakdown | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    // Before the application is saved, show what a filing of this type starts with
    const request = applicationId
      ? getPatentFeeBreakdown(applicationId)
      : getPatentFeePreview(procedureType)

    request
      .then((result) => {
        if (!cancelled) setBreakdown(result)
      })
      .catch((loadError) => {
        if (!cancelled) {
          setBreakdown(null)
          setError(formatApiError(loadError, "The patent fees could not be loaded."))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [applicationId, procedureType, refreshToken])

  const groups = breakdown
    ? [
        breakdown.serviceFees,
        breakdown.procedureFees,
        breakdown.administrativeFees,
        breakdown.extensionValidationFees,
        breakdown.furtherProcessingFees
      ]
    : []

  return (
    <div className="mt-4 w-full shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white text-sm shadow-sm">
      <button
        type="button"
        onClick={toggleOpen}
        className="flex w-full items-center justify-between bg-dantzBlue px-4 py-3 text-white"
        aria-expanded={open}
      >
        <span className="font-medium">Total Fees</span>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold tabular-nums">
            {breakdown ? formatEuroAmount(breakdown.total) : loading ? "…" : "—"}
          </span>
          <FiChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </div>
      </button>

      {open && (
        <div className="space-y-3 bg-secondary1 px-4 py-3 text-xs text-white">
          {!applicationId && (
            <p>
              Starting fees for this filing type. They are updated as you fill in the application.
            </p>
          )}
          {loading && <p><SpinnerText>Loading current fees…</SpinnerText></p>}
          {error && <p className="text-red-100">{error}</p>}
          {groups.map((group) => (
            <PatentFeeGroupRows key={group.label} group={group} />
          ))}
          {breakdown?.deferredFees && (
            <div className="border-t border-secondary3 pt-3">
              <PatentFeeGroupRows group={breakdown.deferredFees} />
            </div>
          )}
          {breakdown && (
            <div className="flex justify-between border-t border-secondary3 pt-2 font-semibold">
              <span>Total</span>
              <span className="tabular-nums">{formatEuroAmount(breakdown.total)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function PatentFeeGroupRows({ group }: { group: PatentFeeGroup }) {
  const selectedLines = group.lines.filter(
    (line) => line.selected && line.amount !== null && line.amount > 0
  )

  if (!selectedLines.length) return null

  return (
    <section className="space-y-1">
      <h3 className="font-semibold">{group.label}</h3>
      {selectedLines.map((line) => {
        if (line.amount === null) return null
        return (
          <div key={line.code} className="flex justify-between gap-3">
            <span className="text-white/80">{line.label}</span>
            <span className="shrink-0 tabular-nums">{formatEuroAmount(line.amount)}</span>
          </div>
        )
      })}
    </section>
  )
}

function formatEuroAmount(amount: number) {
  return <Money amount={amount} />
}
